import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { availableIcons } from '../../utils/profileManager'

import { API_BASE_URL } from '../../config/api'

const AvatarDisplay = ({ profile, isUploading, onFileSelect, onIconSelect, onRemovePicture, onProfileUpdate }) => {
  const [raveName, setRaveName] = useState(profile.raveName || '')
  const [favoriteFestival, setFavoriteFestival] = useState(profile.favoriteFestival || '')
  const [favoriteGenre, setFavoriteGenre] = useState(profile.favoriteGenre || '')
  const [tagline, setTagline] = useState(profile.tagline || '')
  const [isEditing, setIsEditing] = useState(false)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const fileInputRef = useRef()

  // Update state when profile changes
  useEffect(() => {
    setRaveName(profile.raveName || '')
    setFavoriteFestival(profile.favoriteFestival || '')
    setFavoriteGenre(profile.favoriteGenre || '')
    setTagline(profile.tagline || '')
  }, [profile])

  const getCurrentAvatar = () => {
    if (profile.profileType === 'picture' && profile.profilePicture) {
      return (
        <img
          src={profile.profilePicture}
          alt="Profile"
          className="w-full h-full object-cover"
        />
      )
    }

    const selectedIcon = availableIcons.find(icon => icon.id === profile.selectedIcon)
    return (
      <span className="text-5xl">
        {selectedIcon?.icon || '👤'}
      </span>
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await axios.put(`${API_BASE_URL}/auth/settings`, {
        profileSettings: {
          raveName,
          favoriteFestival,
          favoriteGenre,
          tagline
        }
      }, { withCredentials: true })

      setIsEditing(false)

      // Notify parent to refetch profile data
      if (onProfileUpdate) {
        onProfileUpdate()
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to save profile:', error)
      alert('Failed to save profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="bg-gradient-to-br from-white/10 to-white/5 border-2 border-white/30 rounded-xl p-4 sm:p-6 md:p-8 shadow-xl backdrop-blur-sm">
      {/* ID Card Body */}
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 md:gap-8">
        {/* Photo Section */}
        <div className="flex-shrink-0 space-y-3 mx-auto sm:mx-0">
          <div className="w-32 h-40 sm:w-36 sm:h-44 md:w-40 md:h-48 rounded-lg bg-white/10 border-2 border-white/20 flex items-center justify-center overflow-hidden shadow-lg">
            {getCurrentAvatar()}
          </div>

          {/* Rave Alias */}
          <div>
            {isEditing ? (
              <input
                type="text"
                value={raveName}
                onChange={(e) => setRaveName(e.target.value)}
                placeholder="rave alias"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-center"
                style={{color: 'var(--theme-text-primary)'}}
                maxLength={30}
              />
            ) : (
              <div className="text-center text-sm px-3 py-2 bg-black/40 rounded-lg" style={{color: 'var(--theme-text-primary)'}}>
                {raveName || <span style={{color: 'var(--theme-text-muted)'}}>rave alias</span>}
              </div>
            )}
          </div>

          {/* Edit/Save Button */}
          <button
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
            disabled={isSaving}
            className="w-full text-sm px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{color: 'var(--theme-text-primary)'}}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save' : 'Edit'}
          </button>

          {/* Photo Upload & Icon Selection (only in edit mode) */}
          {isEditing && (
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onFileSelect}
                className="hidden"
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full text-white font-medium py-2 px-3 rounded-lg hover:opacity-90 transition-all duration-200 disabled:opacity-50 text-xs"
                style={{background: `linear-gradient(to right, var(--theme-primary), var(--theme-secondary))`}}
              >
                {isUploading ? 'Uploading...' : 'Upload Photo'}
              </button>

              <button
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-full px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg text-xs transition-all duration-200"
                style={{color: 'var(--theme-text-primary)'}}
              >
                {showIconPicker ? 'Hide Icons' : 'Choose Icon'}
              </button>

              {showIconPicker && (
                <div className="max-h-48 overflow-y-auto bg-black/50 backdrop-blur-xl border border-white/30 rounded-lg p-2">
                  <div className="grid grid-cols-4 gap-2">
                    {availableIcons.map((icon) => (
                      <button
                        key={icon.id}
                        onClick={() => {
                          onIconSelect(icon.id)
                          setShowIconPicker(false)
                        }}
                        className={`p-2 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                          profile.selectedIcon === icon.id && profile.profileType === 'icon'
                            ? 'bg-white/20 ring-2 ring-white/40'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                        title={icon.name}
                      >
                        <span className="text-2xl">{icon.icon}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {profile.profilePicture && (
                <button
                  onClick={onRemovePicture}
                  className="w-full text-red-400 hover:text-red-300 text-xs py-1 transition-colors"
                >
                  Remove Picture
                </button>
              )}
            </div>
          )}
        </div>

        {/* Fields Section */}
        <div className="flex-1 space-y-3 sm:space-y-4 w-full">
          <IDField
            label="Favorite Festival"
            value={favoriteFestival}
            onChange={setFavoriteFestival}
            isEditing={isEditing}
            placeholder="e.g., Tomorrowland"
          />
          <IDField
            label="Favorite Genre"
            value={favoriteGenre}
            onChange={setFavoriteGenre}
            isEditing={isEditing}
            placeholder="e.g., House, Techno"
          />
          <IDField
            label="Tagline"
            value={tagline}
            onChange={setTagline}
            isEditing={isEditing}
            placeholder="Your motto"
            multiline
          />
        </div>
      </div>
    </div>
  )
}

const IDField = ({ label, value, onChange, isEditing, placeholder, multiline = false }) => {
  return (
    <div>
      <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2" style={{color: 'var(--theme-text-secondary)'}}>
        {label}
      </label>
      {isEditing ? (
        multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-2 py-1.5 sm:px-3 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-sm sm:text-base resize-none"
            style={{color: 'var(--theme-text-primary)'}}
            rows={2}
            maxLength={60}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-2 py-1.5 sm:px-3 sm:py-2 bg-white/10 border border-white/20 rounded-lg text-sm sm:text-base"
            style={{color: 'var(--theme-text-primary)'}}
            maxLength={30}
          />
        )
      ) : (
        <p className="text-sm sm:text-base px-2 py-1.5 sm:px-3 sm:py-2 bg-black/20 rounded-lg min-h-[36px] sm:min-h-[40px]" style={{color: 'var(--theme-text-primary)'}}>
          {value || <span style={{color: 'var(--theme-text-muted)'}}>{placeholder}</span>}
        </p>
      )}
    </div>
  )
}

export default AvatarDisplay